import { EligibleEntityIdsEditor } from '../EligibleEntityIdsEditor';
import { IdentifyPartyBehaviorSection } from './IdentifyPartyBehaviorSection';
import { IdentifyPartyRoutingSection } from './IdentifyPartyRoutingSection';
import {
  bindConfigPatch,
  bindEligibleIdsChange,
  bindNamedBooleanChange,
  bindPartyRoutesChange,
  patchAdditionalInstructions,
  patchEnableOthersRoute,
  readAdditionalInstructions,
  type IdentifyPartyConfigFormProps,
} from './identifyPartyFormConfig';
import {
  countEntityIds,
  parsePartyRoutes,
  parseUuidList,
  siblingIdSets,
} from './parsePartyRoutes';

export function IdentifyPartyConfigForm(props: IdentifyPartyConfigFormProps) {
  const { config, onConfigChange } = props;
  const reserved = new Set<string>(props.reservedSocketIds);
  const routes = parsePartyRoutes(
    config[props.routesConfigKey],
    props.entityIdKey,
  );
  const siblingSets = siblingIdSets(routes);
  const entityCounts = countEntityIds(routes);
  const eligibleIds = parseUuidList(config[props.eligibleIdsKey]);

  return (
    <div className="space-y-5">
      <div className="space-y-2 pb-2 border-b">
        <div className="flex items-center gap-2">
          {props.headerIcon}
          <span className="text-sm font-medium">{props.headerTitle}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          {props.headerDescription}
        </p>
      </div>

      <IdentifyPartyRoutingSection
        routes={routes}
        siblingSets={siblingSets}
        entityCounts={entityCounts}
        reserved={reserved}
        slugFallback={props.slugFallback}
        duplicateMessage={props.duplicateMessage}
        noEntitySelectedLabel={props.noEntitySelectedLabel}
        addRouteLabel={props.addRouteLabel}
        emptyRoutesHelp={props.emptyRoutesHelp}
        othersHelp={props.othersHelp}
        othersNoMatchHelp={props.othersNoMatchHelp}
        howOutputs={props.howOutputs}
        routingHelp={props.routingHelp}
        enableOthers={config.enableOthersRoute === true}
        othersEmitNullWhenNoMatch={config.othersEmitNullWhenNoMatch === true}
        renderPicker={props.renderPicker}
        EntityName={props.EntityName}
        onRoutesChange={bindPartyRoutesChange({
          config,
          onConfigChange,
          routesConfigKey: props.routesConfigKey,
          entityIdKey: props.entityIdKey,
        })}
        onEnableOthersChange={bindConfigPatch({
          config,
          onConfigChange,
          patch: patchEnableOthersRoute,
        })}
        onOthersEmitNullWhenNoMatchChange={bindNamedBooleanChange({
          config,
          onConfigChange,
          key: 'othersEmitNullWhenNoMatch',
        })}
      />

      <EligibleEntityIdsEditor
        ids={eligibleIds}
        onChange={bindEligibleIdsChange({
          config,
          onConfigChange,
          eligibleIdsKey: props.eligibleIdsKey,
        })}
        sectionLabel={props.allowlistSectionLabel}
        sectionHelp={props.allowlistHelp}
        emptyDescription={props.allowlistEmpty}
        addLabel={props.allowlistAddLabel}
        renderPicker={props.renderPicker}
      />

      <IdentifyPartyBehaviorSection
        useLLMDisambiguation={config.useLLMDisambiguation !== false}
        llmHelp={props.llmHelp}
        extraBehavior={props.extraBehavior}
        additionalInstructions={readAdditionalInstructions(config)}
        additionalInstructionsPlaceholder={
          props.additionalInstructionsPlaceholder
        }
        onUseLLMDisambiguationChange={bindNamedBooleanChange({
          config,
          onConfigChange,
          key: 'useLLMDisambiguation',
        })}
        onAdditionalInstructionsChange={bindConfigPatch({
          config,
          onConfigChange,
          patch: patchAdditionalInstructions,
        })}
      />
    </div>
  );
}
